import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Accordion, Button, Icon, List, Message } from "semantic-ui-react";
import {
    GetLostAlumni,
    GetPledgeClasses,
    GetUser,
    LostAlumnus,
    PledgeClass,
} from "../Utils/UserAPI";
import { LoginInfo } from "../Utils/Login";

export function buildBannerMessage(missingEmailCount: number, missingAddressCount: number): string {
    if (missingEmailCount === 0 && missingAddressCount === 0) {
        return "";
    }

    const pluralize = (n: number) => (n === 1 ? "member" : "members");
    const parts: string[] = [];

    if (missingEmailCount > 0) {
        parts.push(`${missingEmailCount} ${pluralize(missingEmailCount)} of your pledge class missing an email address`);
    }

    if (missingAddressCount > 0) {
        const prefix = parts.length > 0 ? "" : "of your pledge class ";
        parts.push(`${missingAddressCount} ${pluralize(missingAddressCount)} ${prefix}missing mailing addresses`);
    }

    return `Help us find the ${parts.join(" and the ")}.`;
}

function describeMissing(u: LostAlumnus): string {
    const missing = [
        u.missing_email ? "email" : null,
        u.missing_address ? "mailing address" : null,
    ].filter((s): s is string => s !== null);
    return missing.join(" and ");
}

interface LostAlumniBannerProps {
    loginInfo: LoginInfo;
}

export default function LostAlumniBanner({ loginInfo }: LostAlumniBannerProps) {
    const [pledgeClassId, setPledgeClassId] = useState<number>(0);
    const [ownRecordLoaded, setOwnRecordLoaded] = useState(false);
    const [lostAlumni, setLostAlumni] = useState<LostAlumnus[]>([]);
    const [pledgeClasses, setPledgeClasses] = useState<PledgeClass[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [selectedPledgeClassId, setSelectedPledgeClassId] = useState<number>(0);

    // loginInfo.PledgeClassID is not a reliable source (it comes from a Cognito
    // custom claim that this app never actually populates), so fetch the
    // current user's own record to get their real pledge_class_id.
    useEffect(() => {
        if (!loginInfo.id) {
            return;
        }

        let cancelled = false;

        GetUser(loginInfo.id, (statusCode, user) => {
            if (!cancelled && statusCode === 200 && user) {
                setPledgeClassId(user.pledge_class_id);
            }
            if (!cancelled) {
                setOwnRecordLoaded(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [loginInfo.id]);

    useEffect(() => {
        setSelectedPledgeClassId(pledgeClassId);
    }, [pledgeClassId]);

    useEffect(() => {
        if (!pledgeClassId) {
            return;
        }

        let cancelled = false;

        GetLostAlumni((statusCode, data) => {
            if (!cancelled && statusCode === 200) {
                setLostAlumni(data.filter((u) => u.id !== loginInfo.id));
            }
            if (!cancelled) {
                setLoaded(true);
            }
        });

        GetPledgeClasses((statusCode, data) => {
            if (!cancelled && statusCode === 200) {
                setPledgeClasses(data);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [pledgeClassId]);

    if (!ownRecordLoaded || !pledgeClassId || !loaded) {
        return null;
    }

    const ownClassLost = lostAlumni.filter((u) => u.pledge_class_id === pledgeClassId);
    const missingEmailCount = ownClassLost.filter((u) => u.missing_email).length;
    const missingAddressCount = ownClassLost.filter((u) => u.missing_address).length;

    if (missingEmailCount === 0 && missingAddressCount === 0) {
        return null;
    }

    const ownPledgeClass = pledgeClasses.find((pc) => pc.id === pledgeClassId);

    const nearbyClasses = ownPledgeClass
        ? pledgeClasses
            .filter(
                (pc) =>
                    pc.id !== pledgeClassId &&
                    Math.abs(pc.graduation_year - ownPledgeClass.graduation_year) <= 3
            )
            .map((pc) => ({
                pledgeClass: pc,
                count: lostAlumni.filter((u) => u.pledge_class_id === pc.id).length,
            }))
            .filter(({ count }) => count > 0)
        : [];

    const switcherClasses = (
        ownPledgeClass
            ? [{ pledgeClass: ownPledgeClass, count: ownClassLost.length }, ...nearbyClasses]
            : nearbyClasses
    ).sort((a, b) => a.pledgeClass.graduation_year - b.pledgeClass.graduation_year);

    const displayedList = lostAlumni.filter((u) => u.pledge_class_id === selectedPledgeClassId);

    return (
        <Message warning>
            <p>{buildBannerMessage(missingEmailCount, missingAddressCount)}</p>
            <Accordion>
                <Accordion.Title active={expanded} onClick={() => setExpanded(!expanded)}>
                    <Icon name="dropdown" rotated={expanded ? "clockwise" : undefined} />
                    {expanded ? "Hide" : "Show"} the list
                </Accordion.Title>
                <Accordion.Content active={expanded}>
                    {nearbyClasses.length > 0 && (
                        <div style={{ marginBottom: "0.75em" }}>
                            <span style={{ marginRight: "0.5em" }}>Pledge classes:</span>
                            {switcherClasses.map(({ pledgeClass, count }) => (
                                <Button
                                    key={pledgeClass.id}
                                    size="mini"
                                    primary={selectedPledgeClassId === pledgeClass.id}
                                    onClick={() => setSelectedPledgeClassId(pledgeClass.id)}
                                >
                                    {pledgeClass.name} ({count} missing)
                                </Button>
                            ))}
                        </div>
                    )}
                    <List bulleted>
                        {displayedList.map((u) => (
                            <List.Item key={u.id}>
                                <Link to={`/users/${u.id}`}>
                                    {u.first_name} {u.last_name}
                                </Link>
                                {" — missing "}
                                {describeMissing(u)}
                            </List.Item>
                        ))}
                    </List>
                    <p>
                        If you have contact info for any of them, please email{" "}
                        <a href="mailto:help@lambdaphi.org">help@lambdaphi.org</a>.
                    </p>
                </Accordion.Content>
            </Accordion>
        </Message>
    );
}
